"use client";

import { IconLink2Outline18, IconPlusOutline18 } from "nucleo-ui-outline-18";
import ConnectorFormModal from "@/app/(v2)/connectors/_components/ConnectorFormModal";
import { ConnectorLogo } from "@/app/(v2)/connectors/_components/ConnectorLogo";
import { ConnectorOptionRow } from "@/app/(v2)/connectors/_components/ConnectorOptionRow";
import { PipedreamConnectorModal } from "@/app/(v2)/connectors/_components/PipedreamConnectorModal";
import { usePipedreamConnect } from "@/app/(v2)/connectors/_components/usePipedreamConnect";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { RadioGroup } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { showToast } from "@/lib/toast";
import { formatAddress } from "@/lib/utils";
import { useConnectorsStore, type Connector } from "@/stores/connectorsStore";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useEffect, useRef, useState } from "react";

const NONE_OPTION_VALUE = "__none__";

function stripConnectorProviderPrefix(value: string): string {
  return value
    .replace(/(^|\s)[^:\s]+:/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

function abbreviateConnectorName(name: string): string {
  const match = name.match(/^(.+?\s)(0[xz][a-zA-Z0-9]{20,})/);
  if (match) {
    return `${match[1]}${formatAddress(match[2], 8, 6)}`;
  }
  return name;
}

interface ConnectorSelectorProps {
  connectorType: string; // e.g., "slack", "discord"
  value?: string | null; // Selected connector ID, null means "none selected"
  onChange: (connectorId: string | null) => void;
  label?: string;
  description?: string;
  required?: boolean;
}

export default function ConnectorSelector({
  connectorType,
  value,
  onChange,
  label,
  description,
  required,
}: ConnectorSelectorProps) {
  const connectors = useConnectorsStore(state => state.connectors);
  const connectorTypes = useConnectorsStore(state => state.connectorTypes);
  const loadConnectors = useConnectorsStore(state => state.loadConnectors);
  const loadConnectorTypes = useConnectorsStore(state => state.loadConnectorTypes);
  const createConnector = useConnectorsStore(state => state.createConnector);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedConnectorId, setSelectedConnectorId] = useState<string | null>(value ?? null);
  const [showConnectorModal, setShowConnectorModal] = useState(false);
  const [showCustomBotModal, setShowCustomBotModal] = useState(false);
  const [isAutoCreating, setIsAutoCreating] = useState(false);
  const autoCreateAttemptedRef = useRef(false);
  const userInteractedRef = useRef(false);
  const connectorCountBeforeModalRef = useRef(0);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const { pendingConnection, clearPendingConnection } = usePipedreamConnect();

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        await Promise.all([loadConnectors(), loadConnectorTypes()]);
      } catch (error: unknown) {
        console.error("Failed to load connectors", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to load connectors";
        showToast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [loadConnectors, loadConnectorTypes]);

  // Get the logo URL and display name from the connector type definition
  const connectorTypeDefinition = connectorTypes.find(ct => ct.type === connectorType);
  // Human-friendly name: prefer type definition name, otherwise strip provider prefix (e.g., "pipedream:gmail" → "Gmail")
  const connectorDisplayName = stripConnectorProviderPrefix(
    connectorTypeDefinition?.name || connectorType.replace(/^.*:/, "").replace(/^\w/, c => c.toUpperCase()),
  );
  // Effective label: use explicit label if given, otherwise derive from display name
  const effectiveLabel = stripConnectorProviderPrefix(label || `${connectorDisplayName} Connector`);

  useEffect(() => {
    setSelectedConnectorId(value ?? null);
  }, [value]);

  // Filter connectors by type
  const filteredConnectors = connectors.filter(c => c.type === connectorType);

  // A connector is optional if EITHER the type definition says so OR the parent
  // marked it as not-required (e.g., block connectors behind an "if" condition).
  const isOptionalConnector = connectorTypeDefinition?.optional === true || required === false;

  // Auto-create B3OS bot connector when none exists.
  // Skip for optional connectors — don't silently create connectors the user may not need.
  useEffect(() => {
    if (connectorType !== "telegram-bot") return;
    if (isLoading || autoCreateAttemptedRef.current) return;
    if (filteredConnectors.length > 0) return;
    if (isOptionalConnector) return;

    autoCreateAttemptedRef.current = true;
    setIsAutoCreating(true);

    createConnector({
      type: "telegram-bot",
      name: "B3OS Bot",
      description: "",
      data: { mode: "b3os" },
    })
      .then(newConnector => {
        setSelectedConnectorId(newConnector.id);
        onChangeRef.current(newConnector.id);
      })
      .catch(async () => {
        // On conflict (already exists) or other errors, reload and try to select
        await loadConnectors();
        const updated = useConnectorsStore.getState().connectors;
        const match = updated.filter(c => c.type === "telegram-bot");
        if (match.length > 0) {
          const connector = match[match.length - 1];
          setSelectedConnectorId(connector.id);
          onChangeRef.current(connector.id);
        } else {
          // Auto-create failed and no existing connector found; manual fallback UI will show
        }
      })
      .finally(() => setIsAutoCreating(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectorType, isLoading, filteredConnectors.length, createConnector, loadConnectors]);

  // Auto-select the first connector if no value is set and there's only one.
  // Skip auto-selection for optional connectors to let user explicitly choose.
  // Never auto-select after the user has explicitly interacted (select/deselect).
  useEffect(() => {
    if (userInteractedRef.current) return;
    if (!value && filteredConnectors.length === 1 && !isOptionalConnector) {
      const firstConnector = filteredConnectors[0];
      setSelectedConnectorId(firstConnector.id);
      onChangeRef.current(firstConnector.id);
    }
  }, [filteredConnectors, value, isOptionalConnector]);

  const handleSelectConnector = (connector: Connector) => {
    userInteractedRef.current = true;
    setSelectedConnectorId(connector.id);
    onChange(connector.id);
  };

  const handleSelectNone = () => {
    userInteractedRef.current = true;
    setSelectedConnectorId(null);
    onChange(null);
  };

  const refreshAndAutoSelect = async () => {
    await loadConnectors();
    const updatedConnectors = useConnectorsStore.getState().connectors;
    const matchingConnectors = updatedConnectors.filter(c => c.type === connectorType);
    // Only auto-select if a new connector was actually created
    if (matchingConnectors.length > connectorCountBeforeModalRef.current) {
      const newestConnector = matchingConnectors[matchingConnectors.length - 1];
      if (newestConnector) {
        setSelectedConnectorId(newestConnector.id);
        onChange(newestConnector.id);
      }
    }
  };

  const handleModalClose = async () => {
    setShowConnectorModal(false);
    await refreshAndAutoSelect();
  };

  const handleCustomBotModalClose = async () => {
    setShowCustomBotModal(false);
    await refreshAndAutoSelect();
  };

  // Dynamic connector — the ID is a template expression resolved at runtime
  const isDynamicConnector = value != null && value.includes("{{");

  if (isDynamicConnector) {
    return (
      <div className="space-y-2">
        {effectiveLabel && <label className="field-label block">{effectiveLabel}</label>}
        <div className="flex items-center gap-3 rounded-lg border border-blue-300 bg-blue-50 p-3 dark:border-blue-600 dark:bg-blue-900/20">
          <div className="relative flex-shrink-0">
            <ConnectorLogo
              type={connectorType}
              name={connectorDisplayName}
              logoUrl={connectorTypeDefinition?.logoUrl}
            />
            <div className="dark:border-ds-neutral-800 absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-blue-500">
              <IconLink2Outline18 className="h-3 w-3 text-white" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="body-sm-medium">Dynamic Connector</div>
            <div className="mt-0.5 truncate font-mono text-xs text-blue-600 dark:text-blue-400">{value}</div>
            <div className="label-xs mt-1">Resolved at runtime from upstream node</div>
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-ds-text-muted hover:text-ds-text-secondary flex-shrink-0 text-xs transition-colors dark:hover:text-zinc-200"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {effectiveLabel && (
          <label className="field-label block">
            {effectiveLabel}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
        )}
        <div className="text-ds-text-muted flex min-h-24 w-full items-center justify-center gap-2 py-2">
          <PixelLoader size="sm" />
          <span className="body-sm">Loading connectors...</span>
        </div>
      </div>
    );
  }

  const isNoneSelected = selectedConnectorId === null;
  const hasNoConnectors = filteredConnectors.length === 0;

  // Determine what content to render based on state
  const renderContent = () => {
    // For default-only connectors (platform-managed), show a static label with no user controls
    if (connectorTypeDefinition?.defaultOnly) {
      return (
        <div className="space-y-1">
          <span className="body-sm-medium block">{effectiveLabel}</span>
          <div className="bg-ds-surface-card overflow-hidden">
            <div className="flex items-center gap-3 bg-blue-50 px-4 py-3 dark:bg-blue-500/10">
              <div className="flex-shrink-0">
                <ConnectorLogo
                  type={connectorType}
                  name={connectorDisplayName}
                  logoUrl={connectorTypeDefinition?.logoUrl}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="body-sm-medium">Default</div>
                <div className="text-ds-text-muted text-xs">Provided by B3OS</div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // For optional connectors with no configured connectors, show "None" as the default option
    if (hasNoConnectors && isOptionalConnector) {
      return (
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="connector" className="border-none">
            <AccordionTrigger className="px-0 py-2 hover:no-underline">
              <span className="body-sm-medium">
                {effectiveLabel} <span className="text-ds-text-muted font-normal">(Optional)</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-0 pt-0">
              <div className="space-y-2">
                <RadioGroup
                  value={NONE_OPTION_VALUE}
                  onValueChange={() => handleSelectNone()}
                  className="divide-ds-neutral-200 dark:divide-ds-neutral-800 bg-ds-surface-card gap-0 divide-y overflow-hidden"
                >
                  <ConnectorOptionRow
                    optionId={`${connectorType}-default`}
                    value={NONE_OPTION_VALUE}
                    title="Default"
                    subtitle="Provided by B3OS"
                    isSelected
                    connectorType={connectorType}
                    logoName={connectorDisplayName}
                    logoUrl={connectorTypeDefinition?.logoUrl}
                  />
                </RadioGroup>

                {/* Add connector button */}
                <button
                  type="button"
                  onClick={() => {
                    connectorCountBeforeModalRef.current = filteredConnectors.length;
                    setShowConnectorModal(true);
                  }}
                  className="border-ds-neutral-300 text-ds-text-subtle dark:text-ds-text-disabled dark:hover:bg-ds-neutral-800/50 hover:bg-ds-surface-hover w-full rounded-lg border border-dashed p-3 text-center text-sm transition-colors hover:border-zinc-400 dark:border-zinc-600 dark:hover:border-zinc-500"
                >
                  <IconPlusOutline18 className="mr-1 inline h-4 w-4" />
                  Add {connectorDisplayName} connector
                </button>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      );
    }

    // Non-optional connector with no connectors available
    if (hasNoConnectors) {
      // Telegram-bot auto-creation in progress
      if (connectorType === "telegram-bot" && isAutoCreating) {
        return (
          <div className="space-y-2">
            {effectiveLabel && (
              <label className="field-label block">
                {effectiveLabel}
                {required && <span className="ml-1 text-red-500">*</span>}
              </label>
            )}
            <div className="border-ds-border-default dark:bg-ds-neutral-800/50 flex items-center justify-center rounded-lg border bg-zinc-50 p-8">
              <PixelLoader size="md" color="blue" />
              <span className="body-sm ml-2">Setting up Telegram Bot connector...</span>
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-2">
          {effectiveLabel && (
            <label className="field-label block">
              {effectiveLabel}
              {required && <span className="ml-1 text-red-500">*</span>}
            </label>
          )}
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-600 dark:bg-amber-900/20">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex-shrink-0">
                <ConnectorLogo
                  type={connectorType}
                  name={connectorDisplayName}
                  logoUrl={connectorTypeDefinition?.logoUrl}
                />
              </div>
              <p className="body-sm">No {connectorDisplayName} connectors found. Connect one to continue.</p>
            </div>
            <Button
              onClick={() => {
                connectorCountBeforeModalRef.current = filteredConnectors.length;
                setShowConnectorModal(true);
              }}
            >
              <span className="flex items-center gap-2 text-xs">
                <IconPlusOutline18 className="h-3 w-3" />
                Connect {connectorDisplayName}
              </span>
            </Button>
          </div>
        </div>
      );
    }

    // Has connectors - show the list
    const connectorListContent = (
      <div className="space-y-2">
        <RadioGroup
          value={isOptionalConnector ? (selectedConnectorId ?? NONE_OPTION_VALUE) : (selectedConnectorId ?? undefined)}
          onValueChange={nextValue => {
            if (nextValue === NONE_OPTION_VALUE) {
              handleSelectNone();
              return;
            }

            const connector = filteredConnectors.find(item => item.id === nextValue);
            if (connector) {
              handleSelectConnector(connector);
            }
          }}
          className="divide-ds-neutral-200 dark:divide-ds-neutral-800 bg-ds-surface-card gap-0 divide-y overflow-hidden"
        >
          {isOptionalConnector ? (
            <ConnectorOptionRow
              optionId={`${connectorType}-default`}
              value={NONE_OPTION_VALUE}
              title="Default"
              subtitle="Provided by B3OS"
              isSelected={isNoneSelected}
              connectorType={connectorType}
              logoName={connectorDisplayName}
              logoUrl={connectorTypeDefinition?.logoUrl}
            />
          ) : null}

          {filteredConnectors.map(connector => (
            <ConnectorOptionRow
              key={connector.id}
              optionId={connector.id}
              value={connector.id}
              title={abbreviateConnectorName(connector.name)}
              isSelected={selectedConnectorId === connector.id}
              connectorType={connectorType}
              logoName={connector.name}
              logoUrl={connectorTypeDefinition?.logoUrl}
            />
          ))}
        </RadioGroup>

        {/* Add another connector button, or "Advanced" link for telegram-bot */}
        {connectorType === "telegram-bot" ? (
          <button
            type="button"
            onClick={() => {
              connectorCountBeforeModalRef.current = filteredConnectors.length;
              setShowCustomBotModal(true);
            }}
            className="text-ds-text-muted hover:text-ds-text-secondary dark:text-ds-text-muted w-full py-1 text-center text-xs transition-colors dark:hover:text-zinc-300"
          >
            Advanced: connect your own bot
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              connectorCountBeforeModalRef.current = filteredConnectors.length;
              setShowConnectorModal(true);
            }}
            className="border-ds-neutral-300 text-ds-text-subtle dark:text-ds-text-disabled dark:hover:bg-ds-neutral-800/50 hover:bg-ds-surface-hover w-full rounded-lg border border-dashed p-3 text-center text-sm transition-colors hover:border-zinc-400 dark:border-zinc-600 dark:hover:border-zinc-500"
          >
            <IconPlusOutline18 className="mr-1 inline h-4 w-4" />
            Add another {connectorDisplayName} connector
          </button>
        )}
      </div>
    );

    // For optional connectors, wrap in accordion
    // Expand by default if user has selected a non-default connector
    if (isOptionalConnector) {
      return (
        <Accordion
          type="single"
          collapsible
          className="w-full"
          defaultValue={!isNoneSelected ? "connector" : undefined}
        >
          <AccordionItem value="connector" className="border-none">
            <AccordionTrigger className="px-0 py-2 hover:no-underline">
              <span className="body-sm-medium">
                {effectiveLabel} <span className="text-ds-text-muted font-normal">(Optional)</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-0 pt-0">{connectorListContent}</AccordionContent>
          </AccordionItem>
        </Accordion>
      );
    }

    // For required connectors, show the list directly
    return (
      <div className="space-y-2">
        {effectiveLabel && (
          <label className="field-label block">
            {effectiveLabel}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
        )}
        {connectorListContent}
      </div>
    );
  };

  return (
    <>
      {renderContent()}

      {/* Connector Form Modal - rendered once at top level */}
      {showConnectorModal && (
        <ConnectorFormModal
          isOpen={showConnectorModal}
          onClose={handleModalClose}
          connectorTypes={connectorTypes}
          preselectedType={connectorType}
        />
      )}

      {/* Custom Telegram Bot modal */}
      {showCustomBotModal && (
        <ConnectorFormModal
          isOpen={showCustomBotModal}
          onClose={handleCustomBotModalClose}
          connectorTypes={connectorTypes}
          preselectedType="telegram-bot"
          initialTelegramMode="custom"
        />
      )}

      {/* Pipedream OAuth finalization modal - rendered once at top level */}
      {pendingConnection && (
        <PipedreamConnectorModal
          pendingConnection={pendingConnection}
          onClose={() => {
            clearPendingConnection();
            handleModalClose();
          }}
        />
      )}
    </>
  );
}
